import * as React from "react";
import { Check, ChevronsUpDown, Search, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Durchsuchbare WhatsApp-Template-Auswahl.
 * - `dedupeByName` (default true): Sprachvarianten werden zu EINEM Eintrag pro
 *   Template-Namen zusammengefasst (die Sprache wird beim Versand automatisch
 *   nach Kundensprache gewählt). Der gespeicherte Wert ist der Template-NAME.
 * - Tippen filtert die Liste (löst das "zu viele Templates"-Problem).
 */
export function WhatsAppTemplateSelect({
  templates = [],
  value,
  onValueChange,
  placeholder = "Template suchen…",
  dedupeByName = true,
  disabled = false,
  testId = "whatsapp-template-select",
}) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");

  // Optionen aufbauen (dedupe → ein Eintrag pro Name + Sprachliste)
  const options = React.useMemo(() => {
    if (!dedupeByName) {
      return (templates || []).map((t) => ({
        key: t.id || `${t.name}-${t.language}`,
        name: t.name,
        languages: t.language ? [t.language] : [],
        status: t.status,
      }));
    }
    const map = new Map();
    for (const t of templates || []) {
      if (!t?.name) continue;
      const entry = map.get(t.name) || { key: t.name, name: t.name, languages: [], status: t.status };
      if (t.language && !entry.languages.includes(t.language)) entry.languages.push(t.language);
      map.set(t.name, entry);
    }
    return Array.from(map.values());
  }, [templates, dedupeByName]);

  const filtered = React.useMemo(() => {
    if (!searchQuery) return options;
    const q = searchQuery.toLowerCase();
    return options.filter(
      (o) =>
        o.name.toLowerCase().includes(q) ||
        (o.languages || []).some((l) => l.toLowerCase().includes(q))
    );
  }, [options, searchQuery]);

  const selected = options.find((o) => o.name === value);

  const LangBadges = ({ langs }) => (
    <span className="flex flex-wrap gap-1">
      {(langs || []).map((l) => (
        <span
          key={l}
          className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold uppercase"
        >
          {l}
        </span>
      ))}
    </span>
  );

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={disabled}
          data-testid={`${testId}-trigger`}
        >
          {selected ? (
            <span className="flex items-center gap-2 truncate">
              <MessageCircle className="h-4 w-4 text-green-600 shrink-0" />
              <span className="truncate">{selected.name}</span>
              <LangBadges langs={selected.languages} />
            </span>
          ) : (
            <span className="text-muted-foreground">-- Bitte wählen --</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[320px] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              placeholder={placeholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
              data-testid={`${testId}-search`}
            />
          </div>
          <CommandList>
            <CommandEmpty>Kein Template gefunden.</CommandEmpty>
            <CommandGroup>
              {filtered.map((o) => (
                <CommandItem
                  key={o.key}
                  value={o.name}
                  onSelect={() => {
                    onValueChange(o.name);
                    setOpen(false);
                    setSearchQuery("");
                  }}
                  className="cursor-pointer"
                  data-testid={`${testId}-option-${o.name}`}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0",
                      value === o.name ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="flex-1 truncate font-medium">{o.name}</span>
                  <span className="ml-2"><LangBadges langs={o.languages} /></span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
